import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { useDocumentTitle } from '@/shared/lib/use-document-title';
import { NotFoundPage } from '@/widgets/not-found';
import { useOrgContext } from '../../entities/org';
import { useProject } from '../../entities/project';
import { MoveProjectButton } from '../../features/project/move-project';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../shared/ui/breadcrumb';
import { Notice } from '../../shared/ui/notice';
import { PageLayout } from '../../shared/ui/page-layout';
import { TopBarActions, TopBarBreadcrumb } from '../../widgets/app-shell';
import { ScriptProjectView } from './script-project';
import { TitleProjectView } from './title-project';

/**
 * `/o/:orgSlug/w/:workspaceSlug/projects/:projectId`. The project must be in the workspace the URL names;
 * anything else is the standard not-found state. A project is its order: the form while it is a
 * draft, its details and status afterwards.
 */
export function ProjectPage() {
  const { t } = useTranslation(['project', 'workspace']);
  const { workspaceSlug = '', projectId = '' } = useParams();
  const navigate = useNavigate();
  const { org, workspaceBySlug, canIn, activeWorkspaces } = useOrgContext();
  const workspace = workspaceBySlug(workspaceSlug);
  const workspaceId = workspace?.id ?? '';
  const archived = workspace?.archived_at != null;
  const project = useProject(workspaceId, projectId);
  const title = project.data?.name ?? t('detail.title');
  useDocumentTitle(title);

  if (!workspace || project.data === null) return <NotFoundPage />;
  // Targets: other active workspaces where the caller also holds projects.move (move_project checks again).
  const moveTargets =
    canIn(workspace.id, 'projects.move') && !archived
      ? activeWorkspaces.filter((w) => w.id !== workspace.id && canIn(w.id, 'projects.move'))
      : [];

  return (
    <>
      <TopBarBreadcrumb>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={`/o/${org.slug}/w/${workspace.slug}`}>{t('list.breadcrumb')}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </TopBarBreadcrumb>

      {project.data && moveTargets.length > 0 ? (
        <TopBarActions>
          <MoveProjectButton
            project={project.data}
            targets={moveTargets}
            onMoved={(target) =>
              navigate(`/o/${org.slug}/w/${target.slug}/projects/${projectId}`, { replace: true })
            }
          />
        </TopBarActions>
      ) : null}

      <PageLayout title={title}>
        {archived ? <Notice tone="warning">{t('workspace:archivedBanner')}</Notice> : null}
        {project.data?.product === 'title' ? (
          <TitleProjectView project={project.data} workspace={workspace} />
        ) : null}
        {project.data?.product === 'script' ? (
          <ScriptProjectView project={project.data} workspace={workspace} />
        ) : null}
      </PageLayout>
    </>
  );
}
